import React from "react";
import LegalLayout from "@/components/LegalLayout";

export default function Refunds() {
  return (
    <LegalLayout
      eyebrow="// LEGAL"
      title="Refund &amp; Cancellation Policy"
      lastUpdated="February 22, 2026"
    >
      <p>
        This policy explains how cancellations, refunds, and credit balances work
        for <strong>BroadcastFlow AI</strong> subscriptions and credit packs. It
        forms part of, and should be read together with, our
        {" "}<a href="/terms">Terms of Service</a>.
      </p>

      <h2>1. Subscription Plans</h2>

      <h3>1.1 Monthly plans</h3>
      <p>
        Monthly plans are billed in advance for one month of service. You can
        cancel at any time from{" "}<strong>Dashboard → Subscription</strong>. The
        cancellation takes effect at the end of your current billing period &mdash;
        you keep full access until then, and you are not charged again.
      </p>

      <h3>1.2 Annual plans</h3>
      <p>
        Annual plans are billed in advance for 12 months of service. You can
        cancel at any time; access continues until the end of the paid period.
        Annual plans are non-refundable except where required by mandatory
        consumer-protection law in your jurisdiction (see Section 4).
      </p>

      <h3>1.3 Plan changes (upgrades / downgrades)</h3>
      <p>
        Upgrades take effect immediately and are billed pro-rated for the rest of
        the cycle. Downgrades take effect at the next renewal date. We do not
        refund the difference when you downgrade mid-cycle.
      </p>

      <h2>2. Credits</h2>
      <p>
        AI usage on managed providers is metered against your{" "}
        <strong>credit balance</strong> (top-up packs or credits included with
        your plan).
      </p>
      <ul>
        <li>
          Credit packs are non-refundable once any portion has been consumed.
          Unused credits expire <strong>12 months</strong> after purchase.
        </li>
        <li>
          Credits granted as part of a free trial or promotion expire at the end
          of the trial / promotion window.
        </li>
        <li>
          Credits are not transferable between accounts and have no cash value
          outside the Service.
        </li>
        <li>
          If a managed AI provider returns an error and no usable output is
          produced, we refund the credits consumed by that failed call within 7
          days. If you believe a charge was incorrect, contact
          {" "}<a href="mailto:billing@broadcastflow.ai">billing@broadcastflow.ai</a>{" "}within 30 days.
        </li>
      </ul>

      <h2>3. Free Trials</h2>
      <p>
        Where we offer a free trial, you may cancel at any time during the trial
        period to avoid being charged. If you do not cancel, your card will be
        billed automatically at the end of the trial at the displayed plan rate.
        We send a reminder email <strong>at least 3 days</strong> before the
        first charge.
      </p>

      <h2>4. Statutory Right of Withdrawal (EU/UK consumers)</h2>
      <p>
        If you are a consumer resident in the European Economic Area, the United
        Kingdom, or Switzerland, you have a statutory right to withdraw from a
        digital-services contract within <strong>14 days</strong> of purchase. By
        starting to use the Service (including AI generation, publishing, or
        downloads) during that period, you expressly consent to immediate
        performance and{" "}<strong>acknowledge that you will lose the right of
        withdrawal</strong>{" "}once the service has been fully performed or you
        have downloaded/consumed material amounts of content. Where the right is
        still available, you may withdraw by emailing{" "}
        <a href="mailto:billing@broadcastflow.ai">billing@broadcastflow.ai</a>.
      </p>

      <h2>5. Refunds We Do Issue</h2>
      <p>We will issue a pro-rated refund where:</p>
      <ul>
        <li>
          We materially fail to provide the Service (e.g. multi-day outage that
          we cannot resolve) and you cancel as a direct result.
        </li>
        <li>
          A double-charge or billing error is identified within 60 days.
        </li>
        <li>
          You qualify under your local mandatory consumer-protection law.
        </li>
        <li>
          We agree to issue a goodwill refund at our discretion.
        </li>
      </ul>

      <h2>6. How to Cancel</h2>
      <p>
        Sign in and go to{" "}<strong>Dashboard → Subscription</strong>, then click
        <strong>{" "}Cancel plan</strong>. Cancellation is immediate (no future
        charges) and your access remains active until the end of the current
        billing period. You will receive an email confirmation.
      </p>
      <p>
        If you cannot access the dashboard, write to
        {" "}<a href="mailto:billing@broadcastflow.ai">billing@broadcastflow.ai</a>{" "}from
        the address registered on your account.
      </p>

      <h2>7. Account Deletion</h2>
      <p>
        Cancelling your subscription stops future billing but does not by itself
        delete your account or content. To delete your account and all associated
        data, click{" "}<strong>Delete account</strong>{" "}in
        <strong>{" "}Dashboard → Station → Danger zone</strong>{" "}or email
        {" "}<a href="mailto:privacy@broadcastflow.ai">privacy@broadcastflow.ai</a>.
        Once deleted, recovery is not possible.
      </p>

      <h2>8. Chargebacks</h2>
      <p>
        Before initiating a chargeback with your card issuer, please contact us
        first &mdash; almost every dispute is resolved faster by email than by
        the chargeback process. Chargebacks may result in immediate suspension of
        your account pending investigation.
      </p>

      <h2>9. Contact</h2>
      <p>
        Billing questions:{" "}
        <a href="mailto:billing@broadcastflow.ai">billing@broadcastflow.ai</a>
        <br />
        Account / deletion:{" "}
        <a href="mailto:privacy@broadcastflow.ai">privacy@broadcastflow.ai</a>
      </p>

      <p className="text-xs text-ink-muted mt-12 pt-6 border-t border-line">
        This Refund &amp; Cancellation Policy is provided as a starting point and
        has not been reviewed by legal counsel. Mandatory consumer-protection
        rules differ by country; consult a qualified lawyer before relying on
        this policy in production.
      </p>
    </LegalLayout>
  );
}
